<script >
import ChartingLiteChart from '@/features/charting-next/components/ChartingLiteChart.vue';
import { computed } from '@vue/composition-api';
const __sfc_main = {};
__sfc_main.props = {
  stock: {
    type: Object,
    required: true
  },
  resource: {
    type: Object,
    default: () => ({
      status: 'idle',
      data: null,
      error: null
    })
  },
  isActive: {
    type: Boolean,
    default: false
  },
  resolution: {
    type: String,
    default: 'M'
  },
  canUseFavorites: {
    type: Boolean,
    default: false
  },
  isFavoriteMutating: {
    type: Boolean,
    default: false
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const emptyItems = Object.freeze([]);
  const overlayVisibility = Object.freeze({
    support: true,
    stop: false,
    resistance: true,
    trendLine: true,
    trendPoint: true,
    riskRewardPoint: true
  });
  const companyName = computed(() => props.stock.companyName || props.stock.description || 'Sin nombre disponible');
  const favoriteCount = computed(() => props.stock.folderNames?.length || 0);
  const chartData = computed(() => props.resource?.data || null);
  const hasChartData = computed(() => Boolean(chartData.value?.candles?.length));
  const chartErrorMessage = computed(() => {
    const error = props.resource?.error;
    return typeof error === 'string' ? error : error?.message || 'No se pudo cargar el histórico.';
  });
  const operationalMetrics = computed(() => [{
    key: 'price',
    label: 'Precio',
    value: props.stock.priceLabel
  }, {
    key: 'entry',
    label: 'Entrada',
    value: props.stock.entryPriceLabel
  }, {
    key: 'stop',
    label: 'Stop',
    value: props.stock.stopPriceLabel
  }, {
    key: 'support',
    label: 'Soporte',
    value: props.stock.supportPriceLabel
  }, {
    key: 'profitability',
    label: 'Rentab.',
    value: props.stock.profitabilityLabel
  }].filter(item => item.value !== null && item.value !== undefined && String(item.value).trim()));
  const openDetails = () => emit('more-information', props.stock);
  const openChart = () => emit('open-chart', {
    stock: props.stock,
    series: chartData.value
  });
  const handleChartKeydown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openChart();
    }
  };
  return {
    emit,
    emptyItems,
    overlayVisibility,
    companyName,
    favoriteCount,
    chartData,
    hasChartData,
    chartErrorMessage,
    operationalMetrics,
    openDetails,
    openChart,
    handleChartKeydown
  };
};
__sfc_main.components = Object.assign({
  ChartingLiteChart
}, __sfc_main.components);
export default __sfc_main;
</script>

<template>
    <article class="charting-mobile-card" :data-feed-key="stock.ticker">
        <header class="charting-mobile-card__header">
            <div class="charting-mobile-card__identity">
                <div class="charting-mobile-card__ticker-row">
                    <strong>{{ stock.ticker }}</strong>
                    <span v-if="stock.entryTypeLabel && !stock.hasMultipleEntries" class="charting-mobile-card__entry">{{ stock.entryTypeLabel }}</span>
                    <span v-if="stock.platinumScoreLabel" class="charting-mobile-card__score">{{ stock.platinumScoreLabel }} pts</span>
                </div>
                <div class="charting-mobile-card__company">{{ companyName }}</div>
            </div>
            <button
                type="button"
                class="charting-mobile-card__favorite"
                :class="{ 'is-active': favoriteCount, 'is-restricted': !canUseFavorites }"
                :aria-label="canUseFavorites ? `Guardar ${stock.ticker} en favoritos` : 'Favoritos no disponibles en tu plan'"
                :title="canUseFavorites ? 'Guardar en favoritos' : 'Favoritos no disponibles en tu plan'"
                :disabled="isFavoriteMutating"
                @click="emit('open-favorites', stock)"
            >
                <i :class="canUseFavorites ? (favoriteCount ? 'pi pi-star-fill' : 'pi pi-star') : 'pi pi-lock'" aria-hidden="true"></i>
                <span v-if="favoriteCount">{{ favoriteCount }}</span>
            </button>
        </header>

        <div v-if="stock.hasMultipleEntries" class="charting-mobile-card__entries" aria-label="Tipos de entrada">
            <button
                v-for="variant in stock.entryVariants"
                :key="variant.entryVariantKey"
                type="button"
                :class="{ 'is-active': variant.entryVariantKey === stock.activeEntryVariantKey }"
                @click="emit('select-entry-variant', { ticker: stock.ticker, entryVariantKey: variant.entryVariantKey })"
            >
                {{ variant.entrySwitchLabel }}
            </button>
        </div>

        <div class="charting-mobile-card__chart" role="button" tabindex="0" :aria-label="`Abrir gráfico completo de ${stock.ticker}`" @click="openChart" @keydown="handleChartKeydown">
            <div v-if="!isActive || resource.status === 'idle' || resource.status === 'loading'" class="charting-mobile-card__chart-state is-loading" aria-label="Cargando gráfico">
                <ProgressSpinner v-if="isActive" style="width: 28px; height: 28px" stroke-width="6" />
            </div>
            <div v-else-if="resource.status === 'error'" class="charting-mobile-card__chart-state">
                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                <span>{{ chartErrorMessage }}</span>
                <button type="button" @click.stop="emit('retry-chart', stock)">Reintentar</button>
            </div>
            <div v-else-if="!hasChartData" class="charting-mobile-card__chart-state">
                <span>No hay histórico disponible.</span>
            </div>
            <ChartingLiteChart
                v-else
                :candles="chartData.candles"
                :volumes="emptyItems"
                :corporate-actions="emptyItems"
                :analysis-overlays="stock.chartOverlays || {}"
                :overlay-visibility="overlayVisibility"
                :resolution="resolution"
                price-series-type="bars"
                :show-volume="false"
                :show-legend="false"
                :show-current-price-line="false"
                :interactive="false"
                compact
                :ticker="stock.ticker"
                :company-name="companyName"
                :entry-type-label="stock.entryTypeLabel || ''"
                :series-key="`mobile:${stock.ticker}:${resolution}:${stock.activeEntryVariantKey || 'default'}`"
            />
        </div>

        <dl v-if="operationalMetrics.length" class="charting-mobile-card__metrics">
            <div v-for="metric in operationalMetrics" :key="metric.key">
                <dt>{{ metric.label }}</dt>
                <dd>{{ metric.value }}</dd>
            </div>
        </dl>

        <button type="button" class="charting-mobile-card__more" @click="openDetails">
            <span>Más información</span>
            <i class="pi pi-chevron-right" aria-hidden="true"></i>
        </button>
    </article>
</template>

<style scoped>
.charting-mobile-card {
    display: flex;
    min-height: 0;
    flex-direction: column;
    gap: 0.75rem;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 1.25rem;
    background: var(--charting-b-panel-bg-strong);
    padding: 0.8rem;
    box-shadow: 0 10px 30px color-mix(in srgb, #0f172a 7%, transparent);
    scroll-margin-top: 8rem;
}

.charting-mobile-card__header,
.charting-mobile-card__ticker-row,
.charting-mobile-card__entries,
.charting-mobile-card__more {
    display: flex;
    align-items: center;
}

.charting-mobile-card__header {
    justify-content: space-between;
    gap: 0.75rem;
}

.charting-mobile-card__identity { min-width: 0; }
.charting-mobile-card__ticker-row { min-width: 0; gap: 0.45rem; }
.charting-mobile-card__ticker-row strong { font-size: 1rem; }
.charting-mobile-card__company { margin-top: 0.18rem; overflow: hidden; color: var(--p-text-muted-color); font-size: 0.78rem; text-overflow: ellipsis; white-space: nowrap; }

.charting-mobile-card__entry,
.charting-mobile-card__score,
.charting-mobile-card__entries button {
    border: 1px solid var(--charting-b-border-color);
    border-radius: 999px;
    background: var(--charting-b-panel-bg);
    padding: 0.2rem 0.45rem;
    color: var(--p-text-muted-color);
    font-size: 0.65rem;
    font-weight: 800;
}

.charting-mobile-card__score { margin-left: auto; color: var(--brand-primary); }
.charting-mobile-card__entries { flex-wrap: wrap; gap: 0.35rem; }
.charting-mobile-card__entries button { cursor: pointer; }
.charting-mobile-card__entries button.is-active { border-color: var(--brand-primary); background: color-mix(in srgb, var(--brand-primary), transparent 84%); color: var(--brand-primary); }

.charting-mobile-card__favorite {
    display: inline-flex;
    min-width: 2.65rem;
    height: 2.65rem;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    gap: 0.2rem;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 999px;
    background: var(--charting-b-panel-bg);
    color: var(--p-text-muted-color);
    cursor: pointer;
}

.charting-mobile-card__favorite.is-active { border-color: color-mix(in srgb, var(--brand-primary), transparent 45%); color: var(--brand-primary); }
.charting-mobile-card__favorite.is-restricted { opacity: 0.58; }
.charting-mobile-card__favorite:disabled { cursor: wait; opacity: 0.5; }

.charting-mobile-card__chart {
    position: relative;
    min-height: 17rem;
    flex: 0 0 auto;
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border: 1px solid var(--charting-b-border-color);
    border-radius: 1rem;
    background: var(--charting-b-stage-bg);
    cursor: pointer;
}

.charting-mobile-card__chart:focus-visible { outline: 2px solid var(--brand-primary); outline-offset: 2px; }
.charting-mobile-card__chart-state { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 0.55rem; flex-direction: column; padding: 1rem; color: var(--p-text-muted-color); text-align: center; }
.charting-mobile-card__chart-state.is-loading { background: linear-gradient(110deg, var(--charting-b-panel-bg) 8%, var(--charting-b-panel-bg-strong) 18%, var(--charting-b-panel-bg) 33%); background-size: 200% 100%; animation: charting-mobile-skeleton 1.5s linear infinite; }
.charting-mobile-card__chart-state button { border: 0; background: transparent; color: var(--brand-primary); font-weight: 800; cursor: pointer; }

.charting-mobile-card__metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.45rem; margin: 0; }
.charting-mobile-card__metrics > div:last-child:nth-child(odd) { grid-column: 1 / -1; }
.charting-mobile-card__metrics div { min-width: 0; border-radius: 0.75rem; background: var(--charting-b-panel-bg); padding: 0.45rem 0.55rem; }
.charting-mobile-card__metrics dt { color: var(--p-text-muted-color); font-size: 0.63rem; font-weight: 800; text-transform: uppercase; }
.charting-mobile-card__metrics dd { overflow: hidden; margin: 0.18rem 0 0; color: var(--p-text-color); font-size: 0.78rem; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }

.charting-mobile-card__more { width: 100%; min-height: 44px; justify-content: space-between; border: 1px solid var(--charting-b-border-color); border-radius: 0.8rem; background: var(--charting-b-panel-bg); padding: 0.65rem 0.85rem; color: var(--p-text-color); font-weight: 600; cursor: pointer; }
.charting-mobile-card__more:hover { border-color: var(--brand-primary); }
.charting-mobile-card__more:focus-visible { outline: 2px solid var(--brand-primary); outline-offset: 3px; }

@keyframes charting-mobile-skeleton { to { background-position-x: -200%; } }

</style>
